import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import {
  isWebpageRoute,
  ROUTE_TYPES,
  type RouteType,
} from "../../shared/stepConfig";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { useNearViewport } from "../hooks/useNearViewport";
import { useQuery } from "../hooks/useQuery";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useTenantCustomDomain } from "../hooks/useTenantCustomDomain";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import {
  ROUTE_AUTH_BADGE_COLORS,
  routeAuthDescription,
  routeAuthLabel,
} from "../lib/routeAuthDisplay";
import {
  ROUTE_TYPE_ICONS,
  ROUTE_TYPE_LABELS,
  routeTypeLabel,
} from "../lib/routeTypeDisplay";
import { playClick } from "../lib/sounds";
import { getPageUrl } from "../lib/stepUrl";
import { publicDomain, publicUrl } from "../lib/tenantOrigin";
import { makeUrl, routes } from "../lib/urls";
import { writeToClipboard } from "../lib/writeToClipboard";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import type { AppBranch, AppRoute, AppSpace, AppWorkflow } from "./PagesList";
import { cardScope, type CardScope } from "./pageCardRoutes";
import { PageThumbnail } from "./PageThumbnail";
import { MetaSep } from "./MetaSeparator";
import { StatusBadge, type StatusBadgeColor } from "./StatusBadge";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { useToast } from "./Toast";
import { WavyLine } from "./WavyLine";
import { PulseDot } from "./WorkflowSubtext";

const Card = styled("a")`
  all: unset;
  display: flex;
  flex-direction: column;
  gap: 10px;
  cursor: pointer;
  border-radius: 12px;
  text-align: left;
  min-width: 0;
  /* The Links gallery can render hundreds of cards; let the browser skip layout
     and paint of offscreen ones while keeping scroll height stable. */
  content-visibility: auto;
  contain-intrinsic-size: auto 240px auto 232px;

  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const Frame = styled("div")`
  position: relative;
  height: 180px;
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px 24px 0 24px;
  overflow: hidden;
  transition: background 120ms ease;

  [data-active="true"] & {
    background: var(--card-bg-hover);
  }
`;

const PreviewStack = styled("div")`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Preview = styled("div")`
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--surface-secondary);
  outline: 0.5px solid var(--alpha8);
  box-shadow: var(--elevation-low);
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  z-index: 1;
  transform-origin: bottom center;
  transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1);

  [data-active="true"] & {
    transform: scale(1.03);
  }
`;

const PALETTE = ["pink", "purple", "sky", "green", "orange", "red"] as const;

function pickColor(id: string): (typeof PALETTE)[number] {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length]!;
}

function scopeTooltipText({ mode, mixed }: CardScope): string {
  if (!mixed) return routeAuthDescription(mode);
  if (mode === "public") {
    return "These links have different access scopes, including public.";
  }
  if (mode === "external_id") {
    return "These links have different access scopes, and one needs no sign-in.";
  }
  return "These links have different access scopes.";
}

const PreviewPlaceholder = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-primary);
`;

const TypePill = styled("button")`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--alpha8);
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background: var(--alpha16);
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const CopyIconSlot = styled("span")`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: rotate(-180deg) scale(0.5);
  transition:
    opacity 0.15s ease-out,
    transform 0.2s ease-out;
`;

const CopyIconWrap = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  width: 12px;
  height: 12px;

  > ${CopyIconSlot}[data-icon="link"] {
    opacity: 1;
    transform: rotate(0) scale(1);
  }
  &[data-state="copied"] > ${CopyIconSlot}[data-icon="link"] {
    opacity: 0;
    transform: rotate(90deg) scale(0.5);
  }
  &[data-state="copied"] > ${CopyIconSlot}[data-icon="check"] {
    opacity: 1;
    transform: rotate(0) scale(1);
  }
`;

const Overlay = styled("div")`
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 120ms ease;
  [data-active="true"] & {
    opacity: 1;
  }
`;

const OverlayButton = styled("button")`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  outline: 0.5px solid var(--border-alpha);
  color: var(--text-primary);
  cursor: pointer;

  &:hover {
    background: var(--alpha4);
  }
`;

const RouteBadge = styled("div")`
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 2;
`;

const RouteBadgeButton = styled("button")`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--card-badge-bg);
  box-shadow: var(--elevation-low);
  color: #fff;
  cursor: pointer;

  &:hover {
    filter: brightness(1.05);
  }

  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const TitleRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Meta = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0 12px;
`;

const MetaText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const MetaActions = styled("div")`
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 120ms ease;
  [data-active="true"] & {
    opacity: 1;
  }
`;

const MetaButton = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  color: var(--text-secondary);
  cursor: pointer;
  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }
`;

const Subtitle = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: var(--text-secondary);
`;

const BranchStatus = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

type WebpagePreviewProps = {
  workflowId: string;
  branchId: string;
  stepDir: string;
  route: string;
  alt: string;
};

// Each card lazily mounts its step-activity query and live preview iframe the
// first time it nears the viewport, then latches them mounted (`once`):
// rebuilding a torn-down iframe visibly reloads the preview, and
// `content-visibility` already keeps offscreen cards cheap to paint.
function WebpagePreview(props: WebpagePreviewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const near = useNearViewport(ref, { once: true });
  return (
    <PreviewPlaceholder ref={ref}>
      {near ? <WebpagePreviewLoaded {...props} /> : <WavyLine />}
    </PreviewPlaceholder>
  );
}

function WebpagePreviewLoaded({
  workflowId,
  branchId,
  stepDir,
  route,
  alt,
}: WebpagePreviewProps) {
  const [activity] = useQuery(
    queries.loadStepActivityForStep({ workflowId, branchId, stepDir })
  );
  return activity?.latestRenderableExecutionId ? (
    <PageThumbnail
      executionId={activity.latestRenderableExecutionId}
      alt={alt}
      baseRoute={route}
    />
  ) : (
    <WavyLine />
  );
}

const RedactedSpace = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  vertical-align: middle;
`;

type Props = {
  workflow: AppWorkflow;
  branch: AppBranch;
  route: AppRoute;
  // All of the workflow's webpage routes when this card represents the collapsed
  // set; drives the count badge that lists and opens each one.
  webpageRoutes?: AppRoute[];
  space: AppSpace;
  showSpaceColumn: boolean;
  // Redact the space name (show a generic "not in" label) for cards from a
  // space the viewer isn't a member of, on the Links gallery.
  hideSpaceName?: boolean;
  isFavorite: boolean;
  title?: string;
  color?: string;
  isPinned?: boolean;
  onHoverChange?: (hovered: boolean) => void;
  showOpenInBrowserAction?: boolean;
  showFavoriteAction?: boolean;
  showBranchStatus?: boolean;
  showSelectStepAction?: boolean;
  showOpenWorkflowAction?: boolean;
  onToggleFavorite?: () => void;
};

export function PageCard({
  workflow,
  branch,
  route,
  webpageRoutes,
  space,
  showSpaceColumn,
  hideSpaceName = false,
  isFavorite,
  title,
  color: colorOverride,
  isPinned,
  onHoverChange,
  showOpenInBrowserAction,
  showFavoriteAction = true,
  showBranchStatus = true,
  showSelectStepAction = true,
  showOpenWorkflowAction = false,
  onToggleFavorite,
}: Props) {
  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(branch.id);
  const spaceDisplayName = useSpaceDisplayName();
  const { showToast } = useToast();
  const cardRef = useRef<HTMLAnchorElement | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const active = hovered || menuOpen;

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    []
  );

  // Switching tabs / hiding the doc should drop the hover state so returning
  // doesn't leave the card stuck looking active until a mousemove updates.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) setHovered(false);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Close the menu on outside click and on native popover-close events
  // (the latter fires when the user clicks an item or hits Escape).
  useEffect(() => {
    if (!menuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (cardRef.current?.contains(t as Node | null)) return;
      if (t?.closest("[data-popover]")) return;
      setMenuOpen(false);
    };
    const onToggle = (e: Event) => {
      const evt = e as Event & { newState?: string };
      if (evt.newState === "closed") setMenuOpen(false);
    };
    const id = window.setTimeout(() => {
      window.addEventListener("mousedown", onMouseDown);
    }, 0);
    document.addEventListener("toggle", onToggle, true);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("toggle", onToggle, true);
    };
  }, [menuOpen]);

  const routeType = (route.routeType ?? null) as RouteType | null;
  const isWebpage = isWebpageRoute(routeType);
  const scope = cardScope(route, webpageRoutes);
  const scopeBadgeColor: StatusBadgeColor = ROUTE_AUTH_BADGE_COLORS[scope.mode];
  const scopeLabel = scope.mixed ? "Mixed" : routeAuthLabel(scope.mode);
  const scopeTooltip = scopeTooltipText(scope);
  // The link's configured title wins everywhere; then an explicit caller label
  // (the per-workflow Links tab passes the step dir as a fallback); then the
  // workflow name.
  const displayTitle = route.title ?? title ?? workflow.name;
  const canOpenInWorkflowBrowser = showOpenInBrowserAction && isWebpage;
  const isMain = branch.name === "main";
  const customDomain = useTenantCustomDomain();
  const externalPublicUrl = publicUrl();
  const domain = publicDomain();
  const urlFor = (r: AppRoute) =>
    getPageUrl(
      space.slug,
      r.route,
      externalPublicUrl,
      domain,
      {
        branchId: isMain ? null : branch.id,
        externalId: r.externalId,
      },
      customDomain
    );
  const liveUrl = urlFor(route);
  const color = colorOverride ?? (isMain ? pickColor(workflow.id) : "gray");
  const frameStyle = {
    "--card-bg": `var(--${color}-200)`,
    "--card-bg-hover": `var(--${color}-400)`,
    "--card-badge-bg": `var(--${color}-600)`,
  } as React.CSSProperties;

  const markCopied = useCallback(() => {
    setCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
  }, []);

  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  const copyLiveUrl = useCallback(() => {
    playClick();
    void writeToClipboard(liveUrl).then((ok) => {
      if (!ok) return;
      markCopied();
      showToast({
        icon: "check",
        message: "Copied link",
        buttonLabel: "Dismiss",
      });
    });
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  }, [liveUrl, markCopied, showToast]);

  const markViewed = (stepDir: string) => {
    z.mutate(sharedMutators.markWorkflowViewed({ workflowId: workflow.id }));
    z.mutate(
      sharedMutators.markRouteViewed({ workflowId: workflow.id, stepDir })
    );
  };

  const activateCard = () => {
    markViewed(route.stepDir);
    if (isWebpage) {
      playClick();
      cardRef.current?.blur();
      setHovered(false);
    } else {
      copyLiveUrl();
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    activateCard();
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    activateCard();
  };

  const openInWorkflowBrowserUrl = makeUrl(routes.space.workflow, {
    spaceId: space.id,
    workflowId: workflow.id,
    branchId: isMain ? "" : branch.id,
    step: "",
    browserStep: route.stepDir,
    tab: "browser",
  });

  const selectStepUrl = makeUrl(routes.space.workflow, {
    spaceId: space.id,
    workflowId: workflow.id,
    branchId: isMain ? "" : branch.id,
    step: route.stepDir,
  });

  const openWorkflowUrl = makeUrl(routes.space.workflow, {
    spaceId: space.id,
    workflowId: workflow.id,
    branchId: isMain ? "" : branch.id,
    step: "",
  });

  const selectRouteType = (nextRouteType: RouteType) => {
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.updateStepRouteType({
          ...branchParams,
          stepDir: route.stepDir,
          routeType: nextRouteType,
        })
      )
    );
  };

  const togglePin = () => {
    playClick();
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.toggleStepRoutePin({
          ...branchParams,
          stepDir: route.stepDir,
        })
      )
    );
  };

  const pinItem =
    isPinned === undefined
      ? null
      : {
          type: "button" as const,
          key: "toggle-pin",
          left: <Icon name="pin" size={16} />,
          label: isPinned ? "Unpin" : "Pin to top",
          onClick: togglePin,
        };

  const selectTypeItem = showOpenInBrowserAction
    ? {
        type: "submenu" as const,
        key: "select-type",
        left: <Icon name="list-filter" size={16} />,
        label: "Select type",
        submenu: [
          {
            items: ROUTE_TYPES.map((t) => ({
              type: "button" as const,
              key: t,
              left: <Icon name={ROUTE_TYPE_ICONS[t]} size={16} />,
              label: ROUTE_TYPE_LABELS[t],
              right:
                routeType === t ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : null,
              onClick: () => selectRouteType(t),
            })),
          },
        ],
      }
    : null;

  return (
    <Card
      ref={cardRef}
      as={isWebpage ? "a" : "div"}
      href={isWebpage ? liveUrl : undefined}
      target={isWebpage ? "_blank" : undefined}
      rel={isWebpage ? "noopener noreferrer" : undefined}
      role={isWebpage ? undefined : "button"}
      tabIndex={0}
      onMouseDown={isWebpage ? undefined : handleCardClick}
      onClick={isWebpage ? handleCardClick : undefined}
      onKeyDown={isWebpage ? undefined : handleCardKeyDown}
      onMouseEnter={() => {
        setHovered(true);
        onHoverChange?.(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
        onHoverChange?.(false);
      }}
      aria-label={
        isWebpage
          ? `Open ${displayTitle}`
          : `Copy ${routeTypeLabel(routeType)} link for ${displayTitle}`
      }
      data-active={active ? "true" : undefined}
    >
      <Frame style={frameStyle}>
        <PreviewStack>
          <Preview>
            {isWebpage ? (
              <WebpagePreview
                workflowId={workflow.id}
                branchId={branch.id}
                stepDir={route.stepDir}
                route={route.route}
                alt={workflow.name}
              />
            ) : (
              <PreviewPlaceholder>
                <Tooltip content={`Copy ${routeTypeLabel(routeType)} link`}>
                  <TypePill
                    type="button"
                    aria-label={`Copy ${routeTypeLabel(routeType)} link`}
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      e.stopPropagation();
                      copyLiveUrl();
                    }}
                  >
                    <Text variant="text-small-md">
                      {routeTypeLabel(routeType)}
                    </Text>
                    <CopyIconWrap data-state={copied ? "copied" : undefined}>
                      <CopyIconSlot data-icon="link">
                        <Icon name="link-2" size={12} />
                      </CopyIconSlot>
                      <CopyIconSlot data-icon="check">
                        <Icon name="check" size={12} />
                      </CopyIconSlot>
                    </CopyIconWrap>
                  </TypePill>
                </Tooltip>
              </PreviewPlaceholder>
            )}
          </Preview>
          {webpageRoutes && webpageRoutes.length > 1 && (
            <RouteBadge
              onMouseDown={(e) => {
                e.stopPropagation();
                setMenuOpen(true);
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Dropdown
                align="top-left"
                opener={
                  <RouteBadgeButton
                    type="button"
                    aria-label={`${webpageRoutes.length} webpages`}
                  >
                    <Text variant="text-small-md">{webpageRoutes.length}</Text>
                  </RouteBadgeButton>
                }
                sections={[
                  {
                    items: [...webpageRoutes]
                      .sort((a, b) => a.route.localeCompare(b.route))
                      .map((sibling) => ({
                        type: "button" as const,
                        key: sibling.route,
                        left: <Icon name="link-2" size={16} />,
                        label: sibling.route,
                        onClick: () => {
                          markViewed(sibling.stepDir);
                          window.open(
                            urlFor(sibling),
                            "_blank",
                            "noopener,noreferrer"
                          );
                        },
                      })),
                  },
                ]}
              />
            </RouteBadge>
          )}
          <Overlay
            onMouseDown={(e) => {
              e.stopPropagation();
              setMenuOpen(true);
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Dropdown
              align="bottom"
              opener={
                <OverlayButton
                  type="button"
                  aria-label="Page actions"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Icon name="more" size={16} />
                </OverlayButton>
              }
              sections={[
                {
                  items: [
                    ...(canOpenInWorkflowBrowser
                      ? [
                          {
                            type: "link" as const,
                            key: "open-browser",
                            left: <Icon name="earth" size={16} />,
                            label: "Open in workflow browser",
                            to: openInWorkflowBrowserUrl,
                          },
                        ]
                      : []),
                    ...(showOpenWorkflowAction
                      ? [
                          {
                            type: "link" as const,
                            key: "open-workflow",
                            left: <Icon name="product-workflows" size={16} />,
                            label: "Open workflow",
                            to: openWorkflowUrl,
                          },
                        ]
                      : []),
                    ...(pinItem ? [pinItem] : []),
                    ...(showSelectStepAction
                      ? [
                          {
                            type: "link" as const,
                            key: "select-step",
                            left: <Icon name="square-code" size={16} />,
                            label: "Select step",
                            to: selectStepUrl,
                          },
                        ]
                      : []),
                    ...(selectTypeItem ? [selectTypeItem] : []),
                    {
                      type: "button" as const,
                      key: "copy-url",
                      left: <Icon name="link-2" size={16} />,
                      label: "Copy URL",
                      onClick: copyLiveUrl,
                    },
                  ],
                },
              ]}
            />
          </Overlay>
        </PreviewStack>
      </Frame>
      <Meta>
        <MetaText>
          <TitleRow>
            <Text
              variant="text-regular-md"
              color="var(--text-primary)"
              ellipsis
            >
              {displayTitle}
            </Text>
            <Tooltip content={scopeTooltip}>
              <StatusBadge color={scopeBadgeColor}>{scopeLabel}</StatusBadge>
            </Tooltip>
          </TitleRow>
          <Subtitle>
            {showBranchStatus && (
              <BranchStatus>
                {isMain ? (
                  <>
                    <PulseDot paused={workflow.pausedAt != null} />
                    <Text variant="text-small" color="var(--text-secondary)">
                      {workflow.pausedAt != null ? "Paused" : "Live"}
                    </Text>
                  </>
                ) : (
                  <>
                    <Icon name="git-branch" size={10} />
                    <Text variant="text-small" color="var(--text-secondary)">
                      Branch
                    </Text>
                  </>
                )}
              </BranchStatus>
            )}
            {isPinned && (
              <BranchStatus>
                <Icon name="pin-filled" size={10} />
                <Text variant="text-small" color="var(--text-secondary)">
                  Pinned
                </Text>
              </BranchStatus>
            )}
            <Text variant="text-small" color="var(--text-secondary)" ellipsis>
              {(showBranchStatus || isPinned) && <MetaSep />}
              {showSpaceColumn && (
                <>
                  {hideSpaceName ? (
                    <RedactedSpace>
                      <Icon name="eye-closed" size={12} />
                      A space you’re not in
                    </RedactedSpace>
                  ) : (
                    spaceDisplayName(space)
                  )}
                  <MetaSep />
                </>
              )}
              Last updated {formatRelativeTime(workflow.updatedAt)}
            </Text>
          </Subtitle>
        </MetaText>
        <MetaActions
          onMouseDown={(e) => {
            e.stopPropagation();
            setMenuOpen(true);
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Dropdown
            align="bottom"
            minWidth={160}
            opener={
              <MetaButton role="button" aria-label="Page actions">
                <Icon name="more" size={16} />
              </MetaButton>
            }
            sections={[
              {
                items: [
                  ...(canOpenInWorkflowBrowser
                    ? [
                        {
                          type: "link" as const,
                          key: "open-browser",
                          left: <Icon name="earth" size={16} />,
                          label: "Open in workflow browser",
                          to: openInWorkflowBrowserUrl,
                        },
                      ]
                    : []),
                  {
                    type: "button" as const,
                    key: "open-new-tab",
                    left: <Icon name="arrow-up-right" size={16} />,
                    label: "Open in new tab",
                    onClick: () => {
                      playClick();
                      window.open(liveUrl, "_blank", "noopener,noreferrer");
                    },
                  },
                  ...(showOpenWorkflowAction
                    ? [
                        {
                          type: "link" as const,
                          key: "open-workflow",
                          left: <Icon name="product-workflows" size={16} />,
                          label: "Open workflow",
                          to: openWorkflowUrl,
                        },
                      ]
                    : []),
                  ...(pinItem ? [pinItem] : []),
                  ...(showSelectStepAction
                    ? [
                        {
                          type: "link" as const,
                          key: "select-step",
                          left: <Icon name="square-code" size={16} />,
                          label: "Select step",
                          to: selectStepUrl,
                        },
                      ]
                    : []),
                  ...(selectTypeItem ? [selectTypeItem] : []),
                  {
                    type: "button" as const,
                    key: "copy-link",
                    left: <Icon name="link-2" size={16} />,
                    label: "Copy link",
                    onClick: copyLiveUrl,
                  },
                  ...(showFavoriteAction
                    ? [
                        {
                          type: "button" as const,
                          key: "favorite",
                          left: (
                            <Icon
                              name={isFavorite ? "heart-crack" : "heart"}
                              size={16}
                            />
                          ),
                          label: isFavorite ? "Remove favorite" : "Favorite",
                          onClick: () => {
                            playClick();
                            if (onToggleFavorite) {
                              onToggleFavorite();
                            } else {
                              z.mutate(
                                sharedMutators.toggleWorkflowFavorite({
                                  workflowId: workflow.id,
                                })
                              );
                            }
                          },
                        },
                      ]
                    : []),
                ],
              },
            ]}
          />
        </MetaActions>
      </Meta>
    </Card>
  );
}
